import { useEffect, useRef, useState } from "react";
import { MapPin, Eye, EyeOff, Loader2 } from "lucide-react";
import { MapContainer, TileLayer, Marker, Polyline, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import api from "@/lib/api";

// Same icon fix as KmzPanel
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl:       "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl:     "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

function FitToLayer({ bounds }) {
  const map = useMap();
  useEffect(() => {
    if (bounds && bounds.length >= 2) map.fitBounds(bounds, { padding: [25, 25] });
  }, [bounds, map]);
  return null;
}

/**
 * PlanKmzOverlay — split view: blueprint on the left, KMZ map on the right,
 * both synchronized when clicking a path or placemark. When the user
 * calibrates 2 anchor points (click on PDF + input lat/lon), an affine
 * transform is computed and the KMZ is drawn on top of the plan as SVG.
 *
 * Props:
 *   projectId
 *   pages (from estimation)
 */
const COLORS = ["#003366", "#EA580C", "#10b981", "#7c3aed", "#dc2626", "#0891b2"];

export default function PlanKmzOverlay({ projectId, pages = [] }) {
  const [layers, setLayers] = useState([]);
  const [pageIdx, setPageIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activeLayerIdx, setActiveLayerIdx] = useState(0);
  const [calibration, setCalibration] = useState([]); // [{px,py,lat,lon}, ...]
  const [calibrating, setCalibrating] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/projects/${projectId}/kmz`).then(r => {
      if (!cancelled) setLayers(r.data?.items || []);
    }).finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [projectId]);

  const page = pages[pageIdx];
  const active = layers[activeLayerIdx];

  // Affine transform: from (lat, lon) to (image_x, image_y), using two
  // calibration anchors. Assumes small-area near-cartesian mapping.
  const makeTransform = () => {
    if (calibration.length < 2) return null;
    const [a, b] = calibration;
    const dLat = b.lat - a.lat;
    const dLon = b.lon - a.lon;
    const dPx  = b.px  - a.px;
    const dPy  = b.py  - a.py;
    if (Math.abs(dLat) < 1e-9 || Math.abs(dLon) < 1e-9) return null;
    const sx = dPx / dLon;
    const sy = dPy / dLat;
    return (lat, lon) => ({
      x: a.px + (lon - a.lon) * sx,
      y: a.py + (lat - a.lat) * sy,
    });
  };
  const transform = makeTransform();

  const addCalib = async (lat, lon, px, py) => {
    setCalibration(prev => {
      const next = [...prev, { lat, lon, px, py }].slice(-2);
      return next;
    });
  };

  const onPlanClick = async (e) => {
    if (!calibrating) return;
    const rect = imgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const px = (e.clientX - rect.left) / rect.width;
    const py = (e.clientY - rect.top) / rect.height;
    // eslint-disable-next-line no-alert
    const latS = window.prompt("Latitud del punto (decimal, ej. 4.60123):");
    if (!latS) return;
    // eslint-disable-next-line no-alert
    const lonS = window.prompt("Longitud del punto (decimal, ej. -74.08234):");
    if (!lonS) return;
    await addCalib(parseFloat(latS), parseFloat(lonS), px, py);
    if (calibration.length + 1 >= 2) setCalibrating(false);
  };

  // All coords for fitting map bounds
  const allMapCoords = [];
  if (active) {
    (active.placemarks || []).forEach(pm => allMapCoords.push([pm.lat, pm.lon]));
    (active.paths || []).forEach(p => (p.coords || []).forEach(c => allMapCoords.push([c.lat, c.lon])));
  }

  if (loading) return <div className="p-6 text-sm text-slate-500 font-mono">Loading KMZ…</div>;
  if (layers.length === 0) {
    return (
      <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-8 text-center text-sm text-slate-500"
           data-testid="plan-kmz-overlay-empty">
        Sube un KMZ en la pestaña "Mapa KMZ" primero. / Upload a KMZ in the "KMZ Map" tab first.
      </div>
    );
  }

  return (
    <div data-testid="plan-kmz-overlay" className="space-y-3">
      {/* Controls */}
      <div className="bg-white border border-slate-200 rounded-2xl p-3 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <MapPin size={14} className="text-emerald-600" />
          <span className="text-xs font-mono uppercase tracking-wider text-slate-500">Layer:</span>
          {layers.map((l, i) => (
            <button key={l.id || i}
              data-testid={`pkmz-layer-${i}`}
              onClick={() => setActiveLayerIdx(i)}
              className={`text-xs font-mono px-2 py-1 rounded ${
                activeLayerIdx === i ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700"
              }`}>{l.filename}</button>
          ))}
          <span className="text-xs font-mono text-slate-500 ml-2">Page:</span>
          {pages.map((p, i) => (
            <button key={i}
              data-testid={`pkmz-page-${i}`}
              onClick={() => setPageIdx(i)}
              className={`text-xs font-mono px-2 py-1 rounded ${
                pageIdx === i ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700"
              }`}>{i + 1}</button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="pkmz-calibrate-btn"
            onClick={() => setCalibrating(v => !v)}
            className={`text-xs font-mono uppercase tracking-wider px-3 py-1.5 rounded-xl inline-flex items-center gap-1.5 ${
              calibrating ? "bg-[#EA580C] text-white" : "bg-white border border-slate-300 text-slate-700 hover:bg-slate-50"
            }`}>
            {calibrating ? `Click ${calibration.length + 1}/2` : `Calibrar (${calibration.length}/2)`}
          </button>
          {calibration.length > 0 && (
            <button
              data-testid="pkmz-reset-calib"
              onClick={() => { setCalibration([]); setShowOverlay(false); }}
              className="text-xs font-mono uppercase tracking-wider px-2 py-1 rounded bg-slate-100 text-slate-600 hover:bg-slate-200">
              Reset
            </button>
          )}
          <button
            data-testid="pkmz-toggle-overlay"
            onClick={() => setShowOverlay(v => !v)}
            disabled={!transform}
            className="text-xs font-mono uppercase tracking-wider px-3 py-1.5 bg-[#003366] text-white hover:bg-[#002244] rounded-xl inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed">
            {showOverlay ? <EyeOff size={12} /> : <Eye size={12} />}
            {showOverlay ? "Ocultar" : "Overlay en plano"}
          </button>
        </div>
      </div>

      {/* Instructions when calibrating */}
      {calibrating && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3 text-xs font-mono text-amber-900" data-testid="pkmz-calib-help">
          ▸ Click en 2 puntos conocidos del plano (ej. esquinas del edificio).
          Te pediré la lat/lon de cada uno (puedes sacarlos del KMZ o Google Maps).
          Una vez calibrado, el overlay del KMZ se alineará al plano.
          <span className="ml-2">Puntos: {calibration.length}/2</span>
        </div>
      )}

      {/* Split: Plan + Map */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* PLAN with overlay */}
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="pkmz-plan-side">
          <div className="p-2 border-b border-slate-100 text-xs font-mono uppercase tracking-wider text-slate-500">
            Plano · página {pageIdx + 1}
          </div>
          <div className="relative">
            {page?.image_b64 ? (
              <>
                <img
                  ref={imgRef}
                  src={page.image_b64}
                  alt={`Page ${pageIdx + 1}`}
                  onClick={onPlanClick}
                  className={`block w-full h-auto ${calibrating ? "cursor-crosshair" : "cursor-default"}`}
                  data-testid="pkmz-plan-image"
                />
                {/* Calibration markers */}
                <svg className="absolute inset-0 pointer-events-none" viewBox="0 0 1 1" preserveAspectRatio="none"
                     style={{ width: "100%", height: "100%" }}>
                  {calibration.map((c, i) => (
                    <circle key={i} cx={c.px} cy={c.py} r={0.008} fill="#EA580C" stroke="white" strokeWidth={0.002} />
                  ))}
                </svg>
                {/* KMZ overlay */}
                {showOverlay && transform && active && (
                  <svg className="absolute inset-0 pointer-events-none" viewBox="0 0 1 1" preserveAspectRatio="none"
                       style={{ width: "100%", height: "100%" }}
                       data-testid="pkmz-overlay-svg">
                    {(active.paths || []).map((path, pi) => {
                      const pts = (path.coords || [])
                        .map(c => transform(c.lat, c.lon))
                        .filter(p => p.x >= 0 && p.x <= 1 && p.y >= 0 && p.y <= 1);
                      if (pts.length < 2) return null;
                      const d = pts.map((p, i) => (i === 0 ? "M" : "L") + `${p.x} ${p.y}`).join(" ");
                      return <path key={`p-${pi}`} d={d} fill="none"
                                   stroke={COLORS[pi % COLORS.length]}
                                   strokeWidth={0.002} opacity={0.85} />;
                    })}
                    {(active.placemarks || []).slice(0, 300).map((pm, i) => {
                      const p = transform(pm.lat, pm.lon);
                      if (p.x < 0 || p.x > 1 || p.y < 0 || p.y > 1) return null;
                      return <circle key={`pm-${i}`} cx={p.x} cy={p.y} r={0.004}
                                     fill="#10b981" stroke="white" strokeWidth={0.0015} />;
                    })}
                  </svg>
                )}
              </>
            ) : (
              <div className="p-10 text-sm text-slate-500 text-center">No page image available.</div>
            )}
          </div>
        </div>

        {/* MAP */}
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="pkmz-map-side" style={{ minHeight: 400 }}>
          <div className="p-2 border-b border-slate-100 text-xs font-mono uppercase tracking-wider text-slate-500">
            KMZ · {active?.filename || "—"}
          </div>
          {active && allMapCoords.length > 0 ? (
            <div style={{ height: 520 }}>
              <MapContainer center={allMapCoords[0]} zoom={14} scrollWheelZoom style={{ height: "100%", width: "100%" }}>
                <TileLayer attribution="&copy; OSM" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <FitToLayer bounds={allMapCoords} />
                {(active.placemarks || []).slice(0, 300).map((pm, i) => (
                  <Marker key={`map-pm-${i}`} position={[pm.lat, pm.lon]}>
                    <Popup>
                      <div className="text-xs font-mono">
                        <div className="font-semibold">{pm.name || `Node ${i+1}`}</div>
                        <div>{pm.lat.toFixed(5)}, {pm.lon.toFixed(5)}</div>
                      </div>
                    </Popup>
                  </Marker>
                ))}
                {(active.paths || []).map((p, i) => {
                  const positions = (p.coords || []).map(c => [c.lat, c.lon]);
                  if (positions.length < 2) return null;
                  return (
                    <Polyline key={`map-p-${i}`} positions={positions}
                      pathOptions={{ color: COLORS[i % COLORS.length], weight: 3, opacity: 0.85 }} />
                  );
                })}
              </MapContainer>
            </div>
          ) : (
            <div className="p-8 text-sm text-slate-500 text-center">No KMZ selected.</div>
          )}
        </div>
      </div>
    </div>
  );
}
